import { ConditionProps, FORM_MODE, GeoAreaTypes, REST_API } from '@awp/constants';
import * as Yup from 'yup';
import { NotificationContextProvider, useNotificationContext } from './NotificationsContext';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { toast } from 'react-toastify';
import { PostJson, PutJson } from '@awp/helpers/fetchReact';
import { geographicAreaSlice } from '@awp/stores/geographicAreaSlice';
import { allAreaTypesOptions } from './NotificationCommon';

/**
 * Returns a Yup validation schema for a notification form based on dynamic fields and DTO.
 *
 * @param {Array} dynamicFields - The dynamic fields to be validated.
 * @param {object} dto - The data transfer object used for validation.
 * @param {string} mode - The current form mode (e.g., create, edit, subscribe).
 * @returns {Yup.ObjectSchema} The Yup validation schema.
 */
export const YupValidation = (dynamicFields, dto, mode) => {
  return Yup.object().shape({
    name: Yup.string().max(100, 'Maximum 100 characters are allowed').required('Name is required'),
    email: Yup.boolean()
      .nullable()
      .label('Email')
      .test('email_t', 'At least one notification method is selected ', function (value) {
        const { sms } = this.parent;
        if (!sms && !value) return false;
        return true;
      }),
    sms: Yup.boolean()
      .nullable()
      .label('SMS')
      .test('sms_t', 'At least one notification method is selected ', function (value) {
        const { email } = this.parent;
        if (!email && !value) return false;
        return true;
      }),
    startDate: Yup.date()
      .test('startDate', 'Start date is required', function (value) {
        const { enableDateRange } = this.parent;
        if (enableDateRange && !value) {
          return false;
        }
        return true;
      })
      .nullable()
      .label('Start Date'),
    endDate: Yup.date()
      .test('endDate', 'End date is required', function (value) {
        const { enableDateRange } = this.parent;
        if (enableDateRange && !value) {
          return false;
        }
        return true;
      })
      .nullable()
      .label('End Date'),
    frequency: Yup.object().required('Frequency is required').nullable(),
    weatherStations: Yup.array().when(['isPrivate', 'areaType'], {
      is: (isPrivate, areaType) => (isPrivate === 'Y' || mode === FORM_MODE.SUBSCRIBE) && areaType === 'weatherStation',
      then: Yup.array()
        .min(1, 'At least one weather station must be selected')
        .required('At least one weather station must be selected'),
      otherwise: Yup.array().notRequired(),
    }),
    geographicAreaType: Yup.object().when(['isPrivate', 'areaType'], {
      is: (isPrivate, areaType) => (isPrivate === 'Y' || mode === FORM_MODE.SUBSCRIBE) && areaType === 'geographicArea',
      then: Yup.object().required('Geographic area type must be selected').nullable(),
      otherwise: Yup.object().nullable().notRequired(),
    }),
    geographicContractAreas: Yup.array().when(['isPrivate', 'areaType', 'geographicAreaType'], {
      is: (isPrivate, areaType, geographicAreaType) =>
        (isPrivate === 'Y' || mode === FORM_MODE.SUBSCRIBE) &&
        areaType === 'geographicArea' &&
        geographicAreaType?.value === 'CONTRACT AREA',
      then: Yup.array()
        .min(1, 'At least one Contract Area must be selected')
        .required('At least one Contract Area must be selected'),
      otherwise: Yup.array().notRequired(),
    }),
    geographicEETAreas: Yup.array().when(['isPrivate', 'areaType', 'geographicAreaType'], {
      is: (isPrivate, areaType, geographicAreaType) =>
        (isPrivate === 'Y' || mode === FORM_MODE.SUBSCRIBE) &&
        areaType === 'geographicArea' &&
        geographicAreaType?.value === 'EET SERVICE AREA',
      then: Yup.array()
        .min(1, 'At least one EET Service Area must be selected')
        .required('At least one EET Service Area must be selected'),
      otherwise: Yup.array().notRequired(),
    }),

    TriggerGroup: Yup.object().shape(
      dynamicFields.reduce((schema, field) => {
        if (dto.isForMissingObservations) return true; // Skip validation for no data
        if (field.includes('observationType')) {
          schema[field] = Yup.object().required('Observation type is required');
        } else if (field.includes('operator')) {
          schema[field] = Yup.object().required('Operator is required');
        } else if (field.includes('value')) {
          schema[field] = Yup.string()
            .required('Value is required')
            .test('value', 'Invalid value', (value, context) => {
              const dataTypeField = context.path.replace('TriggerGroup.value', 'dataType');
              const dataType = context.parent[dataTypeField];

              const operatorTypeField = context.path.replace('TriggerGroup.value', 'operator');
              const operator = context.parent[operatorTypeField];

              if (isValueEmpty(value)) return false;

              if (dataType !== 'Numeric' && (operator === 'match' || operator?.value === 'match')) {
                try {
                  new RegExp(value);
                  return true;
                } catch (error) {
                  return false;
                }
              } else {
                return true;
              }
            })
            .label('Value');
        } else {
          schema[field] = Yup.mixed().notRequired();
        }
        return schema;
      }, {}),
    ),
  });
};

/**
 * Converts an entity to a data transfer object (DTO) format.
 *
 * @param {object} entity - The entity to be converted.
 * @param {object} notificationMethods - The notification methods (email, sms).
 * @param {Array} frequencies - The list of frequencies.
 * @param {object} weatherStationsObsTypes - The weather stations observation types.
 * @returns {object} The DTO representation of the entity.
 */
export const entityToDto = (
  entity,
  notificationMethods,
  frequencies,
  weatherStationsObsTypes,
  contractAreas,
  eetAreas,
) => {
  const { email, sms } = notificationMethods;

  const weatherStations = weatherStationsObsTypes.options.filter((x) => entity.WeatherStationIds.includes(x.value));
  let geographicAreas = [];

  const contractGeoAreas = contractAreas.filter((x) => entity.GeographicAreaIds.includes(x.value));
  const eetGeoAreas = eetAreas.filter((x) => entity.GeographicAreaIds.includes(x.value));

  geographicAreas = geographicAreas.concat(contractGeoAreas).concat(eetGeoAreas);

  const dtoTemp = {
    id: entity.NotificationTriggerId,
    name: entity.Name,
    enableDateRange: entity.AnnualStartDate && entity.AnnualEndDate ? true : false,
    email,
    sms,
    startDate: new Date(entity.AnnualStartDate || Date.now()),
    endDate: new Date(entity.AnnualEndDate || Date.now()),
    frequency: frequencies.find((x) => x.value === entity.NotificationIntervalHours),
    isPrivate: entity.IsPrivate,
    isActive: entity.IsActive === 'Y',
    isForMissingObservations: entity.IsForMissingObservations === 'Y' ? true : false,
    weatherStations: weatherStations,
    areaType:
      weatherStations.length > 0 ? 'weatherStation' : geographicAreas.length > 0 ? 'geographicArea' : 'weatherStation',
    geographicAreaType:
      contractGeoAreas.length > 0
        ? allAreaTypesOptions.find((x) => x.value === GeoAreaTypes.CONTRACT)
        : eetGeoAreas.length > 0
        ? allAreaTypesOptions.find((x) => x.value === GeoAreaTypes.EET)
        : null,
    geographicContractAreas: contractGeoAreas,
    geographicEETAreas: eetGeoAreas,
  };

  return dtoTemp;
};

/**
 * Converts form values to an entity format suitable for submission.
 *
 * @param {object} values - The form values.
 * @param {object} dto - The data transfer object.
 * @param {string} mode - The current form mode (e.g., create, edit, subscribe).
 * @param {object} currentUser - The current user object containing user details.
 * @returns {object} The entity representation of the form values.
 */
const dtoToEntity = (values, dto, mode, currentUser) => {
  const methods = {
    IsEmailNotify: values.email ? 'Y' : 'N',
    IsSmsNotify: values.sms ? 'Y' : 'N',
  };

  const entity = {
    NotificationTriggerId: values.id ?? 0,
    Name: values.name,
    EnableDateRange: values.enableDateRange,
    AnnualStartDate: values.startDate,
    AnnualEndDate: values.endDate,
    NotificationIntervalHours: values.frequency.value,
    IsActive: values.isActive ? 'Y' : 'N',
    IsPrivate: values.isPrivate,
    TriggerGroup: transformTriggerGroup(values.TriggerGroup, dto),
    ...methods,
  };

  if (values.isPrivate === 'Y' || mode === FORM_MODE.SUBSCRIBE) {
    if (values.areaType === 'weatherStation') {
      entity.NotificationSubscriptions = values.weatherStations.map((weatherStation) => ({
        SecurityIdentifierId: currentUser.SecurityIdentifierId,
        WeatherStationId: weatherStation.value,
        NotificationIntervalHours: values.isForMissingObservations ? values.frequency.value : null, //only for no data
        ...methods,
      }));
    } else if (values.areaType === 'geographicArea') {
      const geographicAreas =
        values.geographicAreaType?.value === 'CONTRACT AREA'
          ? values.geographicContractAreas
          : values.geographicEETAreas;

      if (geographicAreas) {
        entity.NotificationSubscriptions = geographicAreas.map((area) => ({
          SecurityIdentifierId: currentUser.SecurityIdentifierId,
          GeographicAreaId: area.value,
          NotificationIntervalHours: values.isForMissingObservations ? values.frequency.value : null, //only for no data
          ...methods,
        }));
      }
    }
  }

  return entity;
};

/**
 * Transforms the trigger group from form input to the required entity format.
 *
 * @param {object} inputGroup - The input trigger group from the form.
 * @param {object} dto - The data transfer object.
 * @returns {object|null} The transformed trigger group or null if skipping.
 */
const transformTriggerGroup = (inputGroup, dto) => {
  if (dto.isForMissingObservations) return null; // Skip no data
  const outputGroup = {
    TriggerGroupId: inputGroup.groupId,
    IsAnyCondition: inputGroup.isAnyCondition,
    TriggerConditions: inputGroup.TriggerConditions.map((condition) => ({
      TriggerConditionId: condition.conditionId,
      ObservationTypeId: condition.observationType?.value,
      ComparisonOperator: condition.operator?.value,
      ComparisonValue: condition.value,
    })),
    ChildTriggerGroups: inputGroup.ChildTriggerGroups.map((childGroup) => transformTriggerGroup(childGroup, dto)),
  };

  return outputGroup;
};

/**
 * Transforms the values from Formik form to the required format.
 *
 * @param {object} obj - The Formik values.
 * @returns {object} The transformed Formik values.
 */
export const transformFormikValues = (obj) => {
  const triggerGroup = obj.TriggerGroup;
  const result = { ...obj };
  delete result.TriggerGroup;

  const keys = Object.keys(triggerGroup);

  const createChildTriggerGroups = (prefix, level) => {
    const currentKeys = keys.filter((key) => {
      return key.split('_')[1].split('S').length == level && key.startsWith(prefix);
    });

    const childTriggerGroups = currentKeys.map((key) => {
      const isAnyCondition = triggerGroup[key];
      const groupName = key.split('_')[1];
      const uuid = key.split('_')[2];
      const childPrefix = `isAnyCondition_${groupName}S`;
      return {
        isAnyCondition,
        groupId: triggerGroup[`groupId_${groupName}_${uuid}`] ?? 0,
        TriggerConditions: transformConditions(triggerGroup, groupName),
        ChildTriggerGroups: createChildTriggerGroups(childPrefix, level + 1),
      };
    });

    return childTriggerGroups;
  };

  result.TriggerGroup = {
    groupId: triggerGroup['groupId_1_0'] ?? 0,
    isAnyCondition: triggerGroup['isAnyCondition_1_0'],
    TriggerConditions: transformConditions(triggerGroup, '1'),
    ChildTriggerGroups: createChildTriggerGroups('isAnyCondition_1', 2),
  };

  //geographicAreaType == "EET SERVICE AREA" or "CONTRACT AREA"
  //geographicContractAreas array
  //geographicEETAreas array

  return result;
};

/**
 * Transforms conditions from Formik form to the required format.
 *
 * @param {object} triggerGroup - The trigger group from Formik.
 * @param {string} groupName - The name of the group.
 * @returns {Array} The transformed conditions.
 */
const transformConditions = (triggerGroup, groupName) => {
  let conditionsObj = {};

  const keys = Object.keys(triggerGroup);

  keys
    .filter((key) => key.split('_')[1] === groupName)
    .filter((key) => ConditionProps.some((prop) => key.startsWith(prop)))
    .forEach((key) => {
      const [fieldName, groupName, conditionName] = key.split('_');
      conditionsObj[conditionName] = {
        ...conditionsObj[conditionName],
        [fieldName]: triggerGroup[key],
      };
    });

  const conditionsArray = Object.values(conditionsObj);

  return conditionsArray;
};

/**
 * Handles the submission of the notification form.
 *
 * @param {object} param0 - The parameters containing form values and helpers.
 * @param {object} context - The context object containing form mode, state setters, and other relevant data.
 */
export const handleSubmit = ({ values, setSubmitting, resetForm, setTouched, validateForm }, context) => {
  validateForm(values).then((errors) => {
    if (Object.keys(errors).length > 0) {
      setTouched(errors);
      toast.error('Save failed. Please review field error(s).', { autoClose: 5000 });
    } else {
      save(values, setSubmitting, resetForm, context);
    }
  });
};

/**
 * Saves the notification form data.
 *
 * @param {object} values - The form values.
 * @param {function} setSubmitting - The function to control submitting state.
 * @param {function} resetForm - The function to reset the form.
 * @param {object} context - The context object containing form mode, state setters, and other relevant data.
 */
export const save = (values, setSubmitting, resetForm, context) => {
  const { mode, setMode, setNotificationId, notificationId, setErrors, data, setData, currentUser, setReload } =
    context;

  const transformed = transformFormikValues(values);

  setSubmitting(true);
  const entity = dtoToEntity(transformed, context, mode, currentUser);

  if (mode == FORM_MODE.CREATE || mode == FORM_MODE.CLONE) {
    const url = REST_API.NOTIFICATIONS;
    PostJson(url, entity)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Notification has been created');
          const entity = await response.json();
          entity.TriggerCondition = displayAlertLogic(transformed.TriggerGroup);
          updateData(entity, mode, data, setData);
          setNotificationId(entity.NotificationTriggerId);
          setTimeout(() => setMode(FORM_MODE.LIST));
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  } else if (mode === FORM_MODE.EDIT) {
    const url = REST_API.NOTIFICATIONS + notificationId;
    entity.NotificationTriggerId = notificationId;
    PutJson(url, entity)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Notification has been updated');
          const entity = await response.json();
          entity.TriggerCondition = displayAlertLogic(transformed.TriggerGroup);
          updateData(entity, mode, data, setData);
          setTimeout(() => setMode(FORM_MODE.LIST));
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  } else if (mode === FORM_MODE.SUBSCRIBE) {
    const url = REST_API.NOTIFICATIONS + notificationId + '/subscribe';
    entity.NotificationTriggerId = notificationId;
    PutJson(url, {
      IsEmailNotify: entity.IsEmailNotify,
      IsSmsNotify: entity.IsSmsNotify,
      NotificationSubscriptions: entity.NotificationSubscriptions,
    })
      .then(async (response) => {
        if (response.ok) {
          toast.success('Notification has been updated');
          const entity = await response.json();
          entity.TriggerCondition = displayAlertLogic(transformed.TriggerGroup);
          updateData(entity, mode, data, setData);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  }
};

/**
 * Updates the notification data in the state.
 *
 * @param {object} entity - The updated notification entity.
 * @param {string} mode - The current form mode (e.g., edit, subscribe).
 * @param {Array} data - The current array of notification data.
 * @param {function} setData - The function to update the state with the new data.
 */
export const updateData = (entity, mode, data, setData) => {
  if (mode == FORM_MODE.EDIT || mode === FORM_MODE.SUBSCRIBE) {
    const index = data.findIndex((x) => x.NotificationTriggerId === entity.NotificationTriggerId);
    if (index < 0) return;
    data[index] = entity;
    setData([...data]);
  } else {
    setData([...data, entity]);
  }
};

/**
 * Displays the alert logic based on the trigger conditions and child trigger groups.
 *
 * @param {object} obj - The object containing trigger conditions and child trigger groups.
 * @returns {string} The formatted alert logic string.
 */
export const displayAlertLogic = (obj) => {
  let logic = '';
  let count = obj.TriggerConditions.length;
  const operator = obj.isAnyCondition == 'Y' ? ' or ' : ' and ';

  for (let i = 0; i < count; i++) {
    const condition = obj.TriggerConditions[i];
    logic += `'${condition.observationType?.label ?? ''}' ${condition.operator?.label ?? ''} ${condition.value ?? ''}`;
    if (i != count - 1) {
      logic += operator;
    }
  }

  count = obj.ChildTriggerGroups.length;
  for (let i = 0; i < count; i++) {
    const group = obj.ChildTriggerGroups[i];
    let op = operator;
    if (i == 0 && logic === '') {
      op = '';
    }
    logic += `${op}(${displayAlertLogic(group)})`;
  }
  return logic;
};
